import React, { useRef, useEffect, useState } from "react";
import styles from "./HeroSection.module.css";
import Button from "../../Button/Button";
import Lottie from "react-lottie";

interface HeroSectionProps {
  title: string;
  subtitle: string;
  heroButtons?: Array<{
    label: string;
    variant: string;
    onClick?: () => void;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
  }>;
  clutchImages?: Array<{
    image: React.ReactNode;
    link?: string;
  }>;
  groupImages?: Array<{
    image: React.ReactNode;
    link?: string;
  }>;
  heroLottieAnimation?: any;
  heroStaticSvg?: string;
  small?: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  heroButtons = [],
  clutchImages = [],
  groupImages = [],
  heroLottieAnimation,
  small = false,
}) => {
  const lottieRef = useRef<any>(null);
  const [shouldStartAnimation, setShouldStartAnimation] = useState(false);

  useEffect(() => {
    // Delay the animation start by 1 second
    const timer = setTimeout(() => {
      setShouldStartAnimation(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const renderHeroContent = () => {
    const lottieOptions = {
      animationData: heroLottieAnimation,
      loop: false,
      autoplay: shouldStartAnimation,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
        hideOnTransparent: true
      }
    };

    return (
      <div className={styles.animationContainer}>
        <div className={styles.animationInner}>
          <Lottie
            ref={lottieRef}
            options={lottieOptions}
            isClickToPauseDisabled={true}
            eventListeners={[
              {
                eventName: 'complete',
                callback: () => {
                  if (lottieRef.current?.anim) {
                    const totalFrames = lottieRef.current.anim.totalFrames;
                    lottieRef.current.anim.goToAndStop(totalFrames - 1, true);
                  }
                },
              }
            ]}
          />
        </div>
      </div>
    );
  };

  return (
    <section className={`${styles.heroSection} ${small ? styles.small : ""}`}>
      <div className={styles.heroContent}>
        <div className={styles.heroGraphic}>{renderHeroContent()}</div>

        <div className={styles.leftContent}>
          <h1 className={styles.heroTitle}>{title}</h1>
          <p className={styles.heroSubtitle}>{subtitle}</p>
          {heroButtons.length > 0 && (
            <div className={styles.heroButtons}>
              {heroButtons.map((button, index) => (
                <Button
                  key={index}
                  variant={
                    button.variant as
                    | "primary"
                    | "secondary"
                    | "outline"
                    | "outlineNoTheme"
                    | "text"
                    | "text-underline"
                  }
                  onClick={button.onClick}
                  leftIcon={button.leftIcon}
                  rightIcon={button.rightIcon}
                  className={styles.heroButton}
                >
                  {button.label}
                </Button>
              ))}
            </div>
          )}
          {clutchImages.length > 0 && (
            <div className={styles.clutchDiv}>
              {clutchImages.map((clutchImage, index) => (
                <div key={index} className={styles.clutchImage}>
                  {clutchImage.link ? (
                    <a href={clutchImage.link} target="_blank" rel="noopener noreferrer">
                      {clutchImage.image}
                    </a>
                  ) : (
                    clutchImage.image
                  )}
                </div>
              ))}
              {groupImages.length > 0 && (
                <div className={styles.groupImages}>
                  {groupImages.map((groupImage, index) => (
                    <div key={index} className={styles.groupImage}>
                      {groupImage.link ? (
                        <a href={groupImage.link} target="_blank" rel="noopener noreferrer">
                          {groupImage.image}
                        </a>
                      ) : (
                        groupImage.image
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.rightContent}>{renderHeroContent()}</div>
      </div>
    </section>
  );
};

export default HeroSection;
